import React, { useMemo } from 'react';

import styled from 'styled-components';

import { useUserPermission } from '@contexts/UserContext';

import { NwButton, NwLinkButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import SystemMessage from '@components/Utils/SystemMessage';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const BillingInfoContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: 1.4rem;
    margin-top: 0.5rem;

    .billinginfo-container-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.75rem;
        line-height: 1;

        svg {
            opacity: 0.6;
        }
    }

    .billinginfo-container-content {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding-left: 1rem;
        min-height: 2.5rem;
    }

    .billinginfo-container-details {
        p {
            strong {
                font-weight: 600;
            }
        }

        &.inline {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 1rem;
            flex-wrap: nowrap;

            p {
                white-space: nowrap;
            }
        }
    }

    .billinginfo-container-actions {
        font-size: 1rem;

        .change-billing-contact-link {
            line-height: 1.1;
            font-size: 0.85rem;
        }
    }

    p {
        font-size: 0.9rem;
        margin: 0;
        line-height: 1.15em;
    }
`;

const BillingInfoContainerExtended = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    font-size: 2.5rem;
    margin-top: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: ${(props) => props.theme.DefaultBorder};
    padding-left: 1rem;

    .billinginfo-container-icon {
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
            opacity: 0.6;
        }
    }

    .billinginfo-container-details {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding-left: 2rem;

        p {
            font-size: ${(props) => props.theme.FontSizeBase};
            line-height: 1.25em;
            margin: 0 0 0.25rem 0;

            strong {
                font-weight: 600;
            }
        }
    }
`;

const BillingInfo = ({
    data,
    editable,
    onClick,
    buttonLabel,
    extended,
    inline,
    contactName,
    showChangeBillingContactLink = false,
    disableChangeBillingContactLink = false,
    onChangeBillingContact,
}) => {
    const canViewBillingInfos = useUserPermission('ViewBillingInfos');
    const canChangeBillingInfos = useUserPermission('ChangeBillingInfos');

    const addressText = useMemo(() => {
        let text = '';
        if (data.City) {
            text += data.City;
        }
        if (data.Country) {
            if (text) {
                text += ', ';
            }
            text += data.Country;
        }
        return text;
    }, [data.City, data.Country]);

    if (!canViewBillingInfos) {
        return <SystemMessage type="not-allowed" />;
    }

    const showCompanyName = !contactName || data.CompanyName.trim().toLowerCase() !== contactName.trim().toLowerCase();
    const summaryText = addressText ? `${data.InvoiceType} - ${addressText}` : data.InvoiceType;
    const useChangeBillingContactLayout = showChangeBillingContactLink;

    const renderChangeBillingContactLink = () => {
        if (!showChangeBillingContactLink) {
            return null;
        }

        return (
            <NwLinkButton
                className="change-billing-contact-link"
                disabled={!canChangeBillingInfos || disableChangeBillingContactLink || !onChangeBillingContact}
                label="change billing contact"
                onClick={onChangeBillingContact}
            />
        );
    };

    const renderStandardActions = () => {
        if (!editable && !showChangeBillingContactLink) {
            return null;
        }

        return (
            <div className="billinginfo-container-actions">
                {editable && <NwLinkButton disabled={!canChangeBillingInfos} label={buttonLabel} onClick={onClick} />}
                {renderChangeBillingContactLink()}
            </div>
        );
    };

    if (extended) {
        return (
            <BillingInfoContainerExtended className="billinginfo-container">
                <div className="billinginfo-container-icon">
                    <NwIcon icon={byPrefixAndName.fasl['file-invoice-dollar']} />
                </div>
                <div className="billinginfo-container-details">
                    {showCompanyName && (
                        <p>
                            <strong>{data.CompanyName}</strong>
                        </p>
                    )}
                    <p>{summaryText}</p>
                    {editable && (
                        <p>
                            <NwButton disabled={!canChangeBillingInfos} primary label={buttonLabel} onClick={onClick} />
                        </p>
                    )}
                    {renderChangeBillingContactLink()}
                </div>
            </BillingInfoContainerExtended>
        );
    }

    return (
        <BillingInfoContainer className="billinginfo-container">
            <div className="billinginfo-container-icon">
                <NwIcon icon={byPrefixAndName.fasl['file-invoice-dollar']} />
            </div>
            <div className="billinginfo-container-content">
                {useChangeBillingContactLayout ? (
                    <div className="billinginfo-container-details">
                        {showCompanyName && (
                            <p>
                                <strong>{data.CompanyName}</strong>
                            </p>
                        )}
                        <p>{summaryText}</p>
                    </div>
                ) : (
                    <div className={`billinginfo-container-details${inline ? ' inline' : ''}`}>
                        <p>
                            <strong>
                                {showCompanyName ? `${data.CompanyName} - ` : ''}
                                {data.InvoiceType}
                            </strong>
                        </p>
                        {addressText && <p>{addressText}</p>}
                    </div>
                )}
                {renderStandardActions()}
            </div>
        </BillingInfoContainer>
    );
};

export default BillingInfo;
